import React, { useCallback, useEffect, useState } from "react";
import DeleteModalButton from "../../../../components/DeleteModalBtn/DeleteModalButton";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";
import { Modal } from "react-bootstrap";
import Spinner from "components/Spinner";

// Manages Wage Rate Types CRUD + selection
export default function WRateTypes({
  selectedRateType,
  onSelect, // (name)=>void
  selectedStyleId,
  _setRateTypes,
  refreshFunc,
}) {
  const dispatch = useDispatch();
  const [rateTypes, setRateTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ id: null, name: "" });
  const [isEdit, setIsEdit] = useState(false);

  // API
  const getRateTypes = useCallback(async () => {
    try {
      const { data } = await API.post(
        "/rate-types",
        { styleId: selectedStyleId },
        { headers: authHeader() }
      );
      if (data?.success) {
        setRateTypes(data?.data || []);
        _setRateTypes(data?.data || []);
        if (!selectedRateType && (data?.data || []).length) {
          const firstName =
            (data?.data[0]?.name ?? data?.data[0]?.rateType) || data?.data[0];
          onSelect && onSelect(firstName);
        }
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [onSelect, selectedRateType]);

  const addRateType = async (payload) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/rate-type",
        { ...payload, styleId: selectedStyleId },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        dispatch(
          setNotification({ message: "Added rate type", type: "success" })
        );
        await getRateTypes();
        await refreshFunc();
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const updateRateType = async (payload) => {
    try {
      setLoading(true);
      const { data } = await API.patch("/rate-type", payload, {
        headers: authHeader(),
      });
      if (data?.success) {
        dispatch(
          setNotification({ message: "Updated rate type", type: "success" })
        );
        await getRateTypes();
        await refreshFunc();
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const deleteRateType = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete(`/rate-type/${id}`, {
        headers: authHeader(),
      });
      if (data?.success) {
        dispatch(
          setNotification({ message: "Deleted rate type", type: "success" })
        );
        if (rateTypes.find((r) => r.id === id)) {
          const rt = rateTypes.find((r) => r.id === id);
          const name = rt?.name ?? rt?.rateType ?? rt;
          if (name === selectedRateType) onSelect && onSelect("");
        }
        await getRateTypes();
        await refreshFunc();
      } else {
        dispatch(
          setNotification({ message: data?.message || "Failed", type: "error" })
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isEdit) await updateRateType({ id: form.id, name: form.name });
    else await addRateType({ name: form.name });
    setShowModal(false);
    setForm({ id: null, name: "" });
    setIsEdit(false);
  };

  // Sorting Up
  const sortUp = async (id) => {
    const index = rateTypes.findIndex((item) => item.id === id);
    if (index === -1) return;
    if (index === 0) return;
    if (index === rateTypes.length) return;

    const currentSortId = rateTypes[index]?.sortId;
    const currentId = rateTypes[index]?.id;
    const upSortId = rateTypes[index - 1].sortId;
    const upId = rateTypes[index - 1].id;
    try {
      const { data } = await API.post(
        "rate-type/sort-up",
        { currentSortId, currentId, upSortId, upId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getRateTypes();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (id) => {
    const index = rateTypes.findIndex((item) => item.id === id);
    if (index === -1) return;
    if (index === rateTypes.length - 1) return;

    const currentSortId = rateTypes[index]?.sortId;
    const currentId = rateTypes[index]?.id;
    const downSortId = rateTypes[index + 1].sortId;
    const downId = rateTypes[index + 1].id;

    try {
      const { data } = await API.post(
        "rate-type/sort-down",
        { currentSortId, currentId, downSortId, downId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getRateTypes();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedStyleId) {
        getRateTypes();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedStyleId]);

  return (
    <div className="d-flex flex-column h-100">
      {loading && <Spinner />}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Wage Rate Types</h5>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => {
            setIsEdit(false);
            setForm({ id: null, name: "" });
            setShowModal(true);
          }}
        >
          Add
        </button>
      </div>
      <div style={{ overflow: "auto" }} className="flex-grow-1">
        <ul className="list-group">
          {(rateTypes || []).map((rt) => {
            const rtName = rt?.name ?? rt?.rateType ?? rt;
            const isActive = rtName === selectedRateType;
            return (
              <li
                key={rt?.id ?? rtName}
                className={
                  "px-2 py-1 list-group-item d-flex justify-content-between align-items-center " +
                  (isActive ? "list-group-item-primary" : "")
                }
                style={{ cursor: "pointer" }}
                onClick={() => onSelect && onSelect(rtName)}
              >
                <div className="text-truncate" style={{ maxWidth: 200 }}>
                  {rtName}
                </div>
                <div className="btn-group btn-group-sm">
                  <button
                    onClick={() => {
                      rt.sort_id !== 1 && sortUp(rt?.id);
                    }}
                    disabled={rt.sort_id === 1}
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <i className="mdi mdi-chevron-double-up"></i>
                  </button>
                  <button
                    onClick={() => {
                      rt.sort_id !== rateTypes.length && sortDown(rt?.id);
                    }}
                    disabled={rt.sort_id === rateTypes.length}
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <i className="mdi mdi-chevron-double-down"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEdit(true);
                      setForm({ id: rt?.id, name: rtName });
                      setShowModal(true);
                    }}
                  >
                    <i className="mdi mdi-pencil"></i>
                  </button>
                  <DeleteModalButton
                    btnClass={"btn btn-sm btn-outline-danger"}
                    deleteFunc={deleteRateType}
                    deleteParams={rt?.id}
                    disableConditions={loading}
                  />
                </div>
              </li>
            );
          })}
          {(rateTypes || []).length === 0 && (
            <li className="list-group-item text-muted">No rate types</li>
          )}
        </ul>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3"
          style={{ borderRadius: 15 }}
        >
          <h5 className="mb-2">
            {isEdit ? "Edit Rate Type" : "Add Rate Type"}
          </h5>
          <form onSubmit={handleSave}>
            <div className="mb-2">
              <label className="form-label">Name</label>
              <input
                type="text"
                autoFocus
                className="form-control"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                required
              />
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
              <button type="submit" className="btn btn-primary btn-sm ms-1">
                {isEdit ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
